import { type Cell, type Row as TanstackRow, flexRender } from '@tanstack/react-table';
import styled from '@emotion/styled';
import type { VirtualItem } from '@tanstack/react-virtual';
import { type Task } from 'features/taskboard/types/Tasks';
import { Warning } from '@phosphor-icons/react';
import { Tooltip } from 'components';
import { useTableContext } from 'features/misc/table/providers/TableProvider';
import { useUser } from 'features/auth';
import { useTheme } from '@emotion/react';
import { TableConfig } from '../types';
import { styledTheme } from 'styles/theme/theme';
import { DefaultDate } from 'utils';
import { getTotalHeadersSize, isCellEditable } from '../utils';

export type RowProps = {
  row: TanstackRow<unknown>;
  rowIndex: number;
  virtualItem: VirtualItem;
};

type Row = {
  invalid?: boolean;
  viewedOn?: Date | null;
  isTaskUnread: boolean;
};

export const Row = ({ row, rowIndex, virtualItem }: RowProps) => {
  const isSelected = row?.getIsSelected() ? 'selected' : '';
  const currentRow = row?.original as Row;
  const theme = useTheme();

  const { config, table } = useTableContext();

  const tableWidth = getTotalHeadersSize(table);
  const task = row?.original as Task;
  const { data: userData } = useUser({});
  const loggedInUser = userData?.loginContext;

  const viewedOn = task?.viewedOn;
  const isTaskUnread =
    (viewedOn === null || viewedOn === DefaultDate) &&
    task.assignedToPerson?.id === loggedInUser?.id;

  const isInvalidRow = currentRow?.invalid ?? table?.options?.meta?.getInvalidRow?.(row?.original);
  const isSubRow = row?.depth > 0;

  return (
    <tr
      className={isSelected}
      data-row-index={rowIndex}
      style={{
        ...(isSubRow ? { borderTop: 'none' } : {}),
        height: `${virtualItem.size}px`,
        transform: `translateY(${virtualItem.start}px)`,
        transition: 'transform 0.4s ease-in-out',
        backgroundColor: isTaskUnread ? '#F2F4FF' : isInvalidRow ? theme.palette.bg.primary : '',
        zIndex: isSubRow ? 1 : 2,
        minWidth: tableWidth,
      }}
    >
      {row?.getVisibleCells().map((cell, index) => {
        const isFrozen = cell.column.getIsPinned();
        const left = isFrozen ? cell?.column?.getStart('left') : 0;
        const isFirstCell = index === 1;
        const isEditable = isCellEditable(cell.column.columnDef.meta?.editable, cell);
        const tooltipRender = cell.column.columnDef.tooltipRender;
        const border = cell.column.columnDef.border;
        const cellClick = cell.column.columnDef.meta?.onCellClick;
        const isSelector = cell.column.id === 'Selector';

        return (
          <CellDiv
            key={cell.id}
            className={`${isFrozen ? 'frozen ' : ''}${isSelector ? 'selector ' : ''}${
              row?.getIsSelected() ? 'selected' : ''
            }${isTaskUnread ? ' unread' : ''}`}
            isEditable={isEditable}
            noPadding={cell.column.columnDef.meta?.noPadding}
            bold={cell.column.columnDef.meta?.bold}
            onClick={() => {
              if (!isSelector && cellClick && !isEditable) {
                cellClick(cell);
              }
            }}
            style={{
              left,
              width: cell.column.getSize(),
              minWidth: cell.column.getSize(),
              ...(isFirstCell ? { flexGrow: 1 } : {}),
              ...(border === 'left' && {
                borderLeft: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
              }),
              ...(border === 'right' && {
                borderRight: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
              }),
              ...(border === 'both' && {
                borderLeft: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
                borderRight: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
              }),
              opacity: isInvalidRow ? 0.3 : undefined,
            }}
          >
            {tooltipRender && !isInvalidRow ? (
              <Tooltip title={tooltipRender?.(cell) ?? ''}>
                <span>
                  <CellRender cell={cell} config={config} index={index} invalid={isInvalidRow} />
                </span>
              </Tooltip>
            ) : (
              <CellRender cell={cell} config={config} index={index} invalid={isInvalidRow} />
            )}
          </CellDiv>
        );
      })}
    </tr>
  );
};

const CellRender = ({
  invalid,
  index,
  cell,
  config,
}: {
  cell: Cell<unknown, unknown>;
  index: number;
  invalid?: boolean;
  config: TableConfig;
}) => {
  return invalid && index === 1 ? (
    <Icon>
      <Tooltip title={config?.texts?.invalidRow}>
        <Warning size={24} />
      </Tooltip>{' '}
      <Tooltip title={cell?.column?.columnDef?.tooltipRender?.(cell) ?? ''}>
        <span>{flexRender(cell?.column?.columnDef?.cell, cell?.getContext()) ?? ''}</span>
      </Tooltip>
    </Icon>
  ) : (
    flexRender(cell?.column?.columnDef?.cell, cell?.getContext())
  );
};

const Icon = styled.span`
  && {
    gap: 8px;
    display: flex;
    align-items: center;
    > svg {
      flex-shrink: 0;
    }
    > a,
    div,
    span {
      overflow: hidden;
    }
  }
`;

export const CellDiv = styled.td<{
  isEditable?: boolean;
  noPadding?: boolean;
  bold?: boolean;
}>`
  && {
    cursor: ${p => (p.isEditable ? 'pointer' : 'auto')};
    ${p => (p.noPadding ? 'padding: 0;' : '')}
    ${p => (p.bold ? 'font-weight: 600;' : '')}
  }
  .dotsMenuBtn {
    all: unset;
    cursor: pointer;
  }
  &.selector {
    padding: unset;
  }
  .dotsMenu {
    display: none;
    background-color: transparent;
    padding-top: 1px;
    border-radius: 3px;
    height: 18px;
  }
  :hover {
    .dotsMenu {
      display: flex;
    }
  }
`;
